<template>
  <button
    v-bind="$attrs"
    :class="[
      'button-icon',
      `button-icon--size-${size}`,
      `button-icon--theme-${theme}`,
      { 'button-icon--rounded': rounded },
      { 'button-icon--outline': outline },
    ]"
    v-on="$listeners"
  >
    <BaseLoading
      v-if="loading"
      :delay="0"
      class="button-icon__loading"
      :icon-size="size"
    />

    <div v-else class="button-icon__icon">
      <slot />
    </div>

    <BaseBadge v-if="badgeText" class="button-icon__badge">
      {{ badgeText }}
    </BaseBadge>
  </button>
</template>

<script>
import BaseBadge from './BaseBadge.vue'
import BaseLoading from './BaseLoading'

export default {
  components: {
    BaseBadge,
    BaseLoading,
  },
  inheritAttrs: true,
  props: {
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
    theme: {
      type: String,
      required: false,
      default: 'default',
    },
    rounded: {
      type: Boolean,
      required: false,
      default: false,
    },
    outline: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    badgeText: {
      type: String,
      required: false,
      default: undefined,
    },
  },
}
</script>

<style lang="scss" scoped>
.button-icon {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;
  outline-offset: $spacing-5xs;
  transition: background-color 100ms ease-in-out,
    outline-offset 100ms ease-in-out;

  &:active {
    outline-offset: 0;
  }

  &__loading {
    position: absolute;
  }

  &__badge {
    position: absolute;
    top: -$spacing-4xs;
    right: -$spacing-4xs;
  }

  &__icon {
    object-fit: contain;

    :deep(svg, img) {
      display: flex;
      align-self: center;
      width: inherit;
      height: inherit;
    }
  }

  &--size-tiny {
    width: 24px;
    height: 24px;
    border-radius: $border-radius-xs;

    .button-icon__icon {
      width: $spacing-3xs;
      height: $spacing-3xs;
    }
  }

  &--size-small {
    width: 32px;
    height: 32px;
    border-radius: $border-radius-s;

    .button-icon__icon {
      width: $spacing-2xs;
      height: $spacing-2xs;
    }
  }

  &--size-medium {
    width: 38px;
    height: 38px;
    border-radius: $border-radius-m;

    .button-icon__icon {
      width: $spacing-2xs;
      height: $spacing-2xs;
    }
  }

  &--size-large {
    width: 46px;
    height: 46px;
    border-radius: $border-radius-l;

    .button-icon__icon {
      width: $spacing-xs;
      height: $spacing-xs;
    }
  }

  &--rounded {
    border-radius: 100px;
  }

  &--theme-default {
    background: none;
    color: $color-text;

    &:hover {
      background: $color-pebble;
    }

    &:active {
      background: $color-paper;
    }

    &.button-icon--outline {
      border-color: $color-border;
      background: $color-canvas;

      &:hover {
        background: $color-pebble;
      }

      &:active {
        background: $color-canvas;
      }
    }
  }

  &--theme-red {
    background: $color-red-50;
    color: $color-canvas;

    &:hover {
      background: $color-red-60;
    }

    &:active {
      background: $color-red-50;
    }

    &.button-icon--outline {
      border-color: $color-red-40;
      background: $color-canvas;
      color: $color-red-50;

      &:hover {
        background: $color-red-05;
        color: $color-red-60;
      }

      &:active {
        background: $color-canvas;
      }
    }
  }

  &--theme-green {
    background: $color-green-50;
    color: $color-canvas;

    &:hover {
      background: $color-green-60;
    }

    &:active {
      background: $color-green-50;
    }
  }

  &--theme-blue {
    background: $color-blue-45;
    color: $color-canvas;

    &:hover {
      background: $color-blue-60;
    }

    &:active {
      background: $color-blue-45;
    }

    &.button-icon--outline {
      border-color: $color-border;
      background: $color-canvas;
      color: $color-blue-45;

      &:hover {
        background: $color-blue-10;
        color: $color-blue-60;
        border-color: $color-blue-10;
      }

      &:active {
        background: $color-canvas;
        border-color: $color-border;
      }
    }
  }

  &--theme-purple {
    background: $color-purple-50;
    color: $color-canvas;

    &:hover {
      background: $color-purple-60;
    }

    &:active {
      background: $color-purple-50;
    }

    &.button-icon--outline {
      border-color: $color-purple-40;
      background: $color-canvas;
      color: $color-purple-50;

      &:hover {
        background: $color-purple-05;
        color: $color-purple-60;
      }

      &:active {
        background: $color-canvas;
      }
    }
  }

  &:disabled {
    background: $color-disabled;
    color: $color-text-light;
    border-color: transparent;
    pointer-events: none;
    cursor: not-allowed;
  }
}
</style>
